import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextAccounting } from '../Accounting';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { AccountingEntryDA } from '../../../../services/admin/accounting.entry.service';
import { AccountingEntry } from '../../../../models/accounting.entry.model';
import { Error } from '../../../../models/error.model';
import { Contact } from '../../../../models/contact.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { accountingAccountCode, accountingAccountName } from '../../../../scripts/item.account.script';
import { entryTypeLabelKey } from '../../../../scripts/entry.type.label.script';
import AppPreloader from '../../../../components/Preloader';

export interface EntryViewPageProps {};

let errorResponse: Error;

function resolveTitle(entry: AccountingEntry, labels: any): string {
  if (entry.description) return entry.description
  const line = entry.debits?.[0] ?? entry.credits?.[0]
  const type = line?.type
  const docNum = line?.reference?.document_number ?? entry.credits?.[0]?.reference?.document_number ?? ''
  return labels[entryTypeLabelKey(type, docNum)] ?? labels.manualEntry
}

function resolveDocumentNumber(entry: AccountingEntry): string {
  const line = entry.debits?.[0] ?? entry.credits?.[0]
  return line?.reference?.document_number ?? ''
}

function resolveContactName(contacts: Contact[] | null, id_contact: string): string {
  if (!contacts || !id_contact) return ''
  const contact = contacts.find(c => c.id === id_contact)
  if (!contact) return ''
  if (contact.type === 'company') return contact.data?.name ?? ''
  return [contact.data?.first_name, contact.data?.last_name].filter(Boolean).join(' ')
}

const EntryViewPage: React.FunctionComponent<EntryViewPageProps> = () => {
  const {setRoute, companyForUser, accountingAccountsForCompany, contactsForCompany} = UseOutletContextAccounting()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const { id_entry } = useParams<{ id_entry: string }>()

  const [mounted, setMounted] = useState(false)
  const [entry, setEntry] = useState<AccountingEntry | null>(null)

  const loadEntry = async (id_company: string, entryId: string) => {
    try {
      const response = await AccountingEntryDA.getAccountingEntryById(id_company, entryId)
      if (response.status === 200) {
        const raw = response.data
        setEntry({
          ...raw,
          id: raw.id ?? raw._id ?? '',
          id_booking_journal: raw.id_journal ?? raw.id_booking_journal ?? '',
          id_contact: raw.id_contact ?? '',
          created_at: raw.created_at ?? raw.createdAt ?? '',
          updated_at: raw.updated_at ?? raw.updatedAt ?? '',
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  useEffect(() => {
    if (companyForUser && id_entry) {
      loadEntry(companyForUser.id, id_entry)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, id_entry])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.accountingDocument}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  const currency = companyForUser?.currency?.code ?? 'CHF'
  const accounts = accountingAccountsForCompany ?? []

  return (
    <div className="w-100 h-100">
      { companyForUser && entry
        ?
        <>
          {/* Header card */}
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex align-items-center">
                  <div className="d-flex flex-column">
                    <h4 className="m-0">{resolveTitle(entry, lang.labels)}</h4>
                    { resolveDocumentNumber(entry) &&
                      <span className="text-muted fw-bold fs-7 mt-1">{resolveDocumentNumber(entry)}</span>
                    }
                  </div>
                </div>
              </div>
              <div className="card-toolbar">
                <button className="btn btn-sm btn-light-primary" type="button" onClick={() => navigate(-1)}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-3 me-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9.60001 11H21C21.6 11 22 11.4 22 12C22 12.6 21.6 13 21 13H9.60001V11Z" fill="black"/>
                        <path opacity="0.3" d="M9.6 20V4L2.3 11.3C1.9 11.7 1.9 12.3 2.3 12.7L9.6 20Z" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.back}
                  </div>
                </button>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="row">
                <div className="col-12 col-md-6">
                  <table className="table gs-0 gy-1 m-0">
                    <tbody>
                      { entry.entry_number &&
                        <tr>
                          <td className="w-150px fw-bold text-muted">{lang.labels.entryNumber}:</td>
                          <td className="fw-bold">{entry.entry_number}</td>
                        </tr>
                      }
                      <tr>
                        <td className="w-150px fw-bold text-muted">{lang.labels.date}:</td>
                        <td className="fw-bold">{dateFormat(lang.code, entry.date)}</td>
                      </tr>
                      { entry.id_contact &&
                        <tr>
                          <td className="w-150px fw-bold text-muted">{lang.labels.contact}:</td>
                          <td className="fw-bold">{resolveContactName(contactsForCompany, entry.id_contact) || entry.id_contact}</td>
                        </tr>
                      }
                      { entry.description &&
                        <tr>
                          <td className="w-150px fw-bold text-muted">{lang.labels.description}:</td>
                          <td className="fw-bold">{entry.description}</td>
                        </tr>
                      }
                      <tr>
                        <td className="w-150px fw-bold text-muted">{lang.labels.status}:</td>
                        <td>
                          { entry.completed
                            ? <span className="badge badge-light-success">{lang.labels.completed}</span>
                            : <span className="badge badge-light-warning">{lang.labels.pending}</span>
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Lines card */}
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h5 className="m-0">{lang.labels.accountingEntries}</h5>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-0">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-150px">{lang.labels.account}</th>
                      <th className="min-w-200px">{lang.labels.label}</th>
                      <th className="min-w-100px text-end">{lang.labels.debit} ({currency})</th>
                      <th className="min-w-100px text-end">{lang.labels.credit} ({currency})</th>
                    </tr>
                  </thead>
                  <tbody className="text-secondary fw-bold">
                    { entry.debits.map((line, index) => (
                      <tr key={`d-${index}`} className="row-dashed">
                        <td>
                          <span className="fw-bold">{accountingAccountCode(accounts, line.id_account)}</span>
                          <span className="text-muted ms-2 fs-7">{accountingAccountName(accounts, companyForUser.language, line.id_account)}</span>
                        </td>
                        <td>{line.description}</td>
                        <td className="text-end fw-bold">{formatMoney(line.amount)}</td>
                        <td className="text-end"></td>
                      </tr>
                    ))}
                    { entry.credits.map((line, index) => (
                      <tr key={`c-${index}`} className="row-dashed">
                        <td>
                          <span className="fw-bold">{accountingAccountCode(accounts, line.id_account)}</span>
                          <span className="text-muted ms-2 fs-7">{accountingAccountName(accounts, companyForUser.language, line.id_account)}</span>
                        </td>
                        <td>{line.description}</td>
                        <td className="text-end"></td>
                        <td className="text-end fw-bold">{formatMoney(line.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bolder fs-6 border-top">
                      <td colSpan={2} className="text-end">{lang.labels.total}:</td>
                      <td className="text-end">{formatMoney(entry.debits.reduce((sum, d) => sum + d.amount, 0))}</td>
                      <td className="text-end">{formatMoney(entry.credits.reduce((sum, c) => sum + c.amount, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default EntryViewPage;
